import { ChevronDown, Mail, Phone, Plus, SquareUser } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { EditDetail } from './EditDetail';
import { CreateContact } from './CreateContact';
import { Dropdown } from './FieldComponents';
import { formatLastModified } from './NestedFeed';
import { useNavigate } from 'react-router';
import Modal from './Modal';
import Alert from './Alert';
import { getContactInitialsClassName } from '../utils/colorUtils';

const emptyRejectionReview = {
    open: false,
    loading: false,
    saving: false,
    candidates: [],
    selectedLeadIds: [],
    radiusDays: 30,
    error: null,
};

const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'DB';
    return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''}`.toUpperCase();
};

const getProfilePictureUrl = (profilePicture) => {
    if (!profilePicture) return '';
    if (/^https?:\/\//i.test(profilePicture)) return profilePicture;
    return `https://api.casedb.co/${String(profilePicture).replace(/^\/+/, '')}`;
};

const getMatchedDetailLabel = (matchedDetails = []) => {
    const detailTypes = new Set((Array.isArray(matchedDetails) ? matchedDetails : []).map((detail) => (
        String(detail?.type || detail?.label || '').trim().toLowerCase()
    )));
    const hasPhone = Array.from(detailTypes).some((type) => type.includes('phone'));
    const hasEmail = Array.from(detailTypes).some((type) => type.includes('email'));

    if (hasPhone && hasEmail) return 'Same phone number and email address';
    if (hasPhone) return 'Same phone number';
    if (hasEmail) return 'Same email address';
    return matchedDetails.length ? 'Same contact information' : '';
};

const MetaItem = ({ icon, value, main = null, type = null, onClick }) => {
    if (!value) return null;
    return (
        <div className='item' onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
            {icon}
            {type === 'phone' ? <a href={`tel:${value}`}>{value}</a> 
                : type === 'email' ? <a href={`mailto:${main ? main : value}`}>{value}</a> 
                : value}
        </div>
    );
};

export const LeadHeader = ({ leadData = {}, fetchLead }) => {
    const contact = leadData?.contact || {};
    const [editContact, setEditContact] = useState(false);
    const [createContact, setCreateContact] = useState(false);
    const [status, setStatus] = useState(leadData?.status ?? null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [createCase, setCreateCase] = useState({});
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [message, setMessage] = useState(null);
    const [isCreatingCase, setIsCreatingCase] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [rejectionReview, setRejectionReview] = useState(emptyRejectionReview);
    const currentLeadIdRef = useRef(leadData?.id);
    const statusUpdateInFlightRef = useRef(false);
    const statusRequestControllerRef = useRef(null);
    const candidateRequestControllerRef = useRef(null);
    const rejectionRequestControllerRef = useRef(null);
    const navigate = useNavigate();

    currentLeadIdRef.current = leadData?.id;

    const handleNewContactCreated = (newContact) => {
        // Refresh the lead data to show the new contact
        if (fetchLead && typeof fetchLead === 'function') {
            fetchLead(true);
        }
        setCreateContact(false);
        
        if (window.contactCreationCallback && typeof window.contactCreationCallback === 'function') {
            window.contactCreationCallback(newContact);
            window.contactCreationCallback = null;
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        setRejectionReview(emptyRejectionReview);
        setIsUpdatingStatus(false);
        statusUpdateInFlightRef.current = false;

        return () => {
            statusRequestControllerRef.current?.abort();
            candidateRequestControllerRef.current?.abort();
            rejectionRequestControllerRef.current?.abort();
        };
    }, [leadData?.id]);

    const contactNameForInitials = contact?.is_company
        ? (contact?.company_name || contact?.full_name)
        : (contact?.full_name || [contact?.first_name, contact?.last_name].filter(Boolean).join(' '));
    const contactDisplay = contact?.profile_picture
        ? (
            <img
                src={getProfilePictureUrl(contact.profile_picture)}
                alt={`${contact?.full_name || contact?.company_name || 'Lead'} profile`}
            />
        )
        : <h2>{getInitials(contactNameForInitials)}</h2>;

    const isRejectedStatus = (statusId) => leadData?.statuses?.some((statusOption) => (
        Number(statusOption?.id) === Number(statusId)
        && String(statusOption?.name || '').trim().toLowerCase() === 'rejected'
    ));

    const loadRejectionCandidates = async (sourceLeadId = leadData.id, keepOpen = false) => {
        candidateRequestControllerRef.current?.abort();
        const controller = new AbortController();
        candidateRequestControllerRef.current = controller;

        setRejectionReview((current) => ({
            ...current,
            open: keepOpen,
            loading: true,
            saving: false,
            candidates: [],
            selectedLeadIds: [],
            error: null,
        }));

        try {
            const response = await fetch(
                `https://api.casedb.co/leads.php?action=rejection_candidates&lead_id=${encodeURIComponent(sourceLeadId)}`,
                { credentials: 'include', signal: controller.signal }
            );
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Potential related leads could not be loaded.');
            }

            if (controller.signal.aborted || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;

            const candidates = Array.isArray(data.candidates) ? data.candidates : [];
            if (!candidates.length) {
                setRejectionReview(emptyRejectionReview);
                return;
            }

            setRejectionReview((current) => ({
                ...current,
                open: true,
                loading: false,
                candidates,
                radiusDays: Number(data.radius_days) || 30,
            }));
        } catch (error) {
            if (error?.name === 'AbortError' || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;
            console.error('Error loading potential related leads:', error);
            if (keepOpen) {
                setRejectionReview((current) => ({
                    ...current,
                    open: true,
                    loading: false,
                    error: error.message || 'Potential related leads could not be loaded.',
                }));
            } else {
                setRejectionReview(emptyRejectionReview);
                setMessage({
                    message: 'The lead was rejected, but potential related leads could not be checked.',
                    type: 'error',
                });
            }
        }
    };

    const updateStatus = async (nextStatus) => {
        if (statusUpdateInFlightRef.current) return;

        const sourceLeadId = leadData.id;
        const previousStatus = status ?? leadData?.status ?? null;
        statusRequestControllerRef.current?.abort();
        const controller = new AbortController();
        statusRequestControllerRef.current = controller;
        statusUpdateInFlightRef.current = true;
        setIsUpdatingStatus(true);
        setStatus(nextStatus);

        try {
            const response = await fetch(`https://api.casedb.co/leads.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_id: sourceLeadId,
                    status: nextStatus,
                }),
                credentials: 'include',
                signal: controller.signal,
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'The lead status could not be updated.');
            }

            if (controller.signal.aborted || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;

            try {
                await fetchLead?.();
            } catch (refreshError) {
                console.error('Lead status was updated, but the refreshed lead data could not be loaded:', refreshError);
            }
            if (isRejectedStatus(nextStatus)) {
                await loadRejectionCandidates(sourceLeadId);
            }
        } catch (error) {
            if (error?.name === 'AbortError' || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;
            console.error('Error updating status:', error);
            setStatus(previousStatus);
            setMessage({
                message: error.message || 'The lead status could not be updated.',
                type: 'error',
            });
        } finally {
            if (Number(currentLeadIdRef.current) === Number(sourceLeadId)) {
                statusUpdateInFlightRef.current = false;
                setIsUpdatingStatus(false);
            }
        }
    };

    const toggleRejectionCandidate = (leadId) => {
        const normalizedId = Number(leadId);
        setRejectionReview((current) => ({
            ...current,
            selectedLeadIds: current.selectedLeadIds.includes(normalizedId)
                ? current.selectedLeadIds.filter((id) => id !== normalizedId)
                : [...current.selectedLeadIds, normalizedId],
        }));
    };

    const finishRejectionReview = async () => {
        if (!rejectionReview.selectedLeadIds.length) {
            setRejectionReview(emptyRejectionReview);
            return;
        }

        const relatedLeads = rejectionReview.selectedLeadIds.map((leadId) => {
            const candidate = rejectionReview.candidates.find((item) => Number(item.lead_id) === Number(leadId));
            return {
                lead_id: leadId,
                expected_status: candidate?.status?.id ?? null,
            };
        });

        const sourceLeadId = leadData.id;
        rejectionRequestControllerRef.current?.abort();
        const controller = new AbortController();
        rejectionRequestControllerRef.current = controller;
        setRejectionReview((current) => ({ ...current, saving: true, error: null }));

        try {
            const response = await fetch('https://api.casedb.co/leads.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'reject_related',
                    lead_id: sourceLeadId,
                    related_leads: relatedLeads,
                }),
                signal: controller.signal,
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                const staleMessage = data.reason_code === 'stale_lead_status'
                    ? 'One or more leads changed after this list loaded. Refresh the matches and review them again.'
                    : null;
                throw new Error(staleMessage || data.message || 'The selected leads could not be rejected.');
            }

            if (controller.signal.aborted || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;

            const affectedCount = Array.isArray(data.affected_lead_ids) ? data.affected_lead_ids.length : relatedLeads.length;
            setRejectionReview(emptyRejectionReview);
            setMessage({
                message: `${affectedCount} related lead${affectedCount === 1 ? '' : 's'} marked as rejected.`,
                type: 'success',
            });
            try {
                await fetchLead?.();
            } catch (refreshError) {
                console.error('Related leads were rejected, but the refreshed lead data could not be loaded:', refreshError);
            }
        } catch (error) {
            if (error?.name === 'AbortError' || Number(currentLeadIdRef.current) !== Number(sourceLeadId)) return;
            console.error('Error rejecting related leads:', error);
            setRejectionReview((current) => ({
                ...current,
                saving: false,
                error: error.message || 'The selected leads could not be rejected.',
            }));
        }
    };

    const deleteLeadHandler = async () => {
        fetch(`https://api.casedb.co/lead.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                delete: true,
                lead_id: leadData.id
            })
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                navigate('/intake');
            } else {
                console.error("Error deleting lead:", data.message);
            }
        })
        .catch(error => {
            console.error("Error deleting lead:", error);
        });
    };

    const createCaseHandler = async () => {
        if (isCreatingCase) return; // Prevent duplicate calls
        
        setIsCreatingCase(true);
        
        const payload = {
            lead_id: leadData.id,
            case_name: createCase.case_name ?? leadData.contact.full_name,
            contact_id: leadData.contact.id,
            pfs: leadData.pfs,
            create: true,
            case_type_id: leadData.case_type_id,
        };

        try {
            const response = await fetch("https://api.casedb.co/lead.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                navigate(`/case/${result.case_id}`);
            } else {
                console.error("Error creating case:", result.message);
                setMessage({ message: "Failed to create case: " + result.message, type: "error" });
            }
        }
        catch (error) {
            console.error("Error creating case:", error);
            setMessage({ message: "Error creating case. Please try again.", type: "error" });
        } finally {
            setIsCreatingCase(false);
        }
    };

    const primaryEmail = contact?.emails && contact.emails.length > 0 ? contact.emails[0].email : "";
    const primaryPhone = contact?.phones && contact.phones.length > 0 ? contact.phones[0].number : "";

    useEffect(() => {
        setStatus(leadData?.status ?? null);
    }, [leadData?.id, leadData?.status]);

    const statusOptions = leadData?.statuses?.filter(statusOption => statusOption?.substatus === null)?.map(statusOption => [statusOption.id, statusOption.name]) || [];
    const caseCreated = (Array.isArray(leadData?.cases) && leadData.cases.length > 0)
        || (Array.isArray(leadData?.lead?.cases) && leadData.lead.cases.length > 0);
    const lastUpdatedValue = leadData?.last_updated ?? leadData?.updated_at ?? leadData?.status_date ?? leadData?.created_at;
    const lastUpdatedParts = formatLastModified(lastUpdatedValue);
    const lastUpdated = lastUpdatedParts?.[5] || 'Not available';
    const lastUpdatedIso = lastUpdatedParts?.[1] || undefined;

    const leadActions = !caseCreated && (
        <div className='lead-actions'>
            <button
                type='button'
                className={`action-tiny tertiary ${isCreatingCase ? 'disabled' : ''}`}
                onClick={() => !isCreatingCase && setCreateCase({ create: true })}
                disabled={isCreatingCase}
            >
                {isCreatingCase ? 'Creating...' : 'Case'} <Plus size={16} />
            </button>
            <button type='button' className='action-tiny destroy' onClick={() => setShowDeleteModal(true)}>
                Delete
            </button>
        </div>
    );

    const statusControl = (
        <div className={`lead-status-control${isUpdatingStatus ? ' updating' : ''}`} aria-busy={isUpdatingStatus}>
            <div className='case-phase'>
                <Dropdown
                    options={statusOptions}
                    onChange={updateStatus}
                    value={status ?? leadData?.status ?? ''}
                />
            </div>
            <time className='lead-last-updated subtext' dateTime={lastUpdatedIso} title={lastUpdated}>
                Last updated {lastUpdated}
            </time>
        </div>
    );

    return (
        <div className={`lead-header`}>
            <div className='lead-information-container'>
                <div className='lead-information'>
                    <div className={getContactInitialsClassName('case-initials', leadData?.id)}>
                        {contactDisplay}
                    </div>
                    <div className='details'>
                        <h2 className='lead-header-title'>
                            <span>{contact?.full_name || "Loading..."}</span>
                            <span className='subtext'> ({leadData?.id || "N/A"}{Number(leadData?.deleted || 0) ? ' - This lead has been deleted.' : ''})</span>
                        </h2>
                        {screenWidth >= 768 && (
                            <div className='lead-header-meta subtext'>
                                <>
                                    <MetaItem icon={<SquareUser size={16} />} value={contact?.full_name} onClick={() => setEditContact(true)} />
                                    <MetaItem icon={<Phone size={16} />} value={primaryPhone} type='phone' />
                                    <MetaItem icon={<Mail size={16} />} value={primaryEmail} type='email' />
                                </>
                            </div>
                        )}
                    </div>
                </div>
                {screenWidth < 768 ? (
                    <div className='lead-header-mobile-controls'>
                        <div className='lead-header-meta subtext'>
                            <div className='filter-group'>
                                <div className='select-wrapper'>
                                    <select className='filter-select'>
                                        <option value={primaryPhone}>{primaryPhone}</option>
                                        <option value={primaryEmail}>{primaryEmail}</option>
                                    </select>
                                    <ChevronDown size={16} className='select-chevron' />
                                </div>
                            </div>
                        </div>
                        <div className='lead-header-controls'>
                            {leadActions}
                            {statusControl}
                        </div>
                    </div>
                ) : (
                    <div className='lead-header-controls'>
                        {leadActions}
                        {statusControl}
                    </div>
                )}
            </div>
            {editContact && (
                <EditDetail 
                    setEditContact={setEditContact} 
                    contactData={contact} 
                    fetchContacts={fetchLead} 
                    setCreateContact={setCreateContact}
                />
            )}

            {createCase.create && (
                <Modal
                    title='Create Case'
                    instructions={'To update the case name, change the name below. Otherwise, the case name will be the same as the lead name.'}
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                className='action alt' 
                                onClick={() => setCreateCase(false)}
                                disabled={isCreatingCase}
                            >
                                Cancel
                            </button>
                            <button 
                                className='action primary' 
                                onClick={createCaseHandler}
                                disabled={isCreatingCase}
                                style={{ 
                                    opacity: isCreatingCase ? 0.6 : 1,
                                    cursor: isCreatingCase ? 'not-allowed' : 'pointer' 
                                }}
                            >
                                {isCreatingCase ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className='subtext'>Case Name</label>
                            <input 
                                type='text' 
                                placeholder='Case Name' 
                                value={createCase.case_name !== undefined ? createCase.case_name : leadData.contact.full_name}
                                onChange={(e) => setCreateCase({ ...createCase, case_name: e.target.value })} 
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {showDeleteModal && (
                <Modal
                    title='Delete Lead'
                    instructions={`Are you sure you want to delete this lead? This action cannot be undone.`}
                    onClose={() => setShowDeleteModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button className='action alt' onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className='action cancel' onClick={() => {
                                setShowDeleteModal(false);
                                deleteLeadHandler();
                            }}>Delete Lead</button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <p style={{ margin: '0' }}>Lead: <strong>{leadData?.contact?.full_name || 'Unknown'}</strong></p>
                        <p style={{ margin: '0' }}>Lead ID: <strong>#{leadData?.id}</strong></p>
                    </div>
                </Modal>
            )}
            {rejectionReview.open && (
                <Modal
                    wide
                    title='Review Related Leads'
                    instructions={`These leads were created within ${rejectionReview.radiusDays} days and share a phone number or email address with this lead. Select any that should also be rejected. No additional leads change until you choose Done.`}
                    onClose={() => !rejectionReview.saving && setRejectionReview(emptyRejectionReview)}
                    footer={
                        <div className='modal-footer-actions lead-rejection-footer'>
                            <span className='subtext'>
                                {rejectionReview.selectedLeadIds.length
                                    ? `${rejectionReview.selectedLeadIds.length} additional lead${rejectionReview.selectedLeadIds.length === 1 ? '' : 's'} selected`
                                    : 'No additional leads selected'}
                            </span>
                            <button
                                type='button'
                                className='action'
                                onClick={finishRejectionReview}
                                disabled={rejectionReview.loading || rejectionReview.saving}
                            >
                                {rejectionReview.saving ? 'Saving...' : 'Done'}
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper lead-rejection-review'>
                        {rejectionReview.loading && (
                            <div className='lead-rejection-state subtext'>Checking for potential related leads...</div>
                        )}

                        {!rejectionReview.loading && rejectionReview.error && (
                            <div className='lead-rejection-state error-state'>
                                <span>{rejectionReview.error}</span>
                                <button type='button' className='action alt' onClick={() => loadRejectionCandidates(leadData.id, true)}>
                                    Refresh matches
                                </button>
                            </div>
                        )}

                        {!rejectionReview.loading && !rejectionReview.error && !rejectionReview.candidates.length && (
                            <div className='lead-rejection-state subtext'>
                                No potential duplicate or related leads were found.
                            </div>
                        )}

                        {!rejectionReview.loading && rejectionReview.candidates.map((candidate) => {
                            const candidateId = Number(candidate.lead_id);
                            const selected = rejectionReview.selectedLeadIds.includes(candidateId);
                            const pictureUrl = getProfilePictureUrl(candidate.profile_picture);
                            const candidateDate = formatLastModified(candidate.created_at)?.[3] || 'Unknown date';

                            return (
                                <div
                                    className={`mini-case-summary lead-rejection-candidate${selected ? ' selected' : ''}`}
                                    key={candidate.lead_id}
                                >
                                    <div className={getContactInitialsClassName('mini-case-initials', candidate.contact_id || candidate.lead_id)}>
                                        {pictureUrl
                                            ? <img src={pictureUrl} alt='' />
                                            : getInitials(candidate.contact_name)}
                                    </div>
                                    <div className='mini-case-meta lead-rejection-candidate-meta'>
                                        <div className='mini-case-name'>
                                            <a href={`/intake/${candidate.lead_id}`} target='_blank' rel='noreferrer'>
                                                {candidate.contact_name || 'Unnamed lead'}
                                            </a>
                                            <span className='mini-case-id subtext'>({candidate.lead_id})</span>
                                        </div>
                                        <div className='mini-case-info subtext'>
                                            <span>{candidate.status?.name || 'Unknown status'}</span>
                                            <span>Created {candidateDate}</span>
                                            {candidate.case_type_name && <span>{candidate.case_type_name}</span>}
                                            {candidate.assigned_to_name && <span>{candidate.assigned_to_name}</span>}
                                        </div>
                                        {!!getMatchedDetailLabel(candidate.matched_details) && (
                                            <div className='lead-rejection-matches'>
                                                <span className='subtext'>{getMatchedDetailLabel(candidate.matched_details)}</span>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type='button'
                                        className={`action ${selected ? 'alt' : 'destroy'} lead-rejection-toggle`}
                                        aria-pressed={selected}
                                        onClick={() => toggleRejectionCandidate(candidate.lead_id)}
                                    >
                                        {selected ? 'Cancel' : 'Reject'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </Modal>
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated} 
                />
            )}
            {message && <Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />}
        </div>
    );
};
